import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, ArrowLeft, Save, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const emptyVoce = () => ({ id: null, code: "", description: "", unit: "m", unit_price: "" });

export default function ListinoEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", active: true, kind: "attivo", voci: [emptyVoce()] });
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [units, setUnits] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/listini/import-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "template_listino.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore nel download del template"); }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/listini/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imported = data.voci.map((v) => ({ id: null, code: v.code || "", description: v.description, unit: v.unit, unit_price: v.unit_price }));
      setForm((prev) => {
        const existing = prev.voci.filter((v) => v.description.trim() || (v.code || "").trim());
        const merged = [...existing];
        const idxByCode = {};
        merged.forEach((v, i) => { const c = (v.code || "").trim(); if (c) idxByCode[c] = i; });
        let added = 0, updated = 0;
        imported.forEach((imp) => {
          const c = (imp.code || "").trim();
          if (c && idxByCode[c] !== undefined) {
            const t = merged[idxByCode[c]];
            merged[idxByCode[c]] = { ...t, code: imp.code, description: imp.description, unit: imp.unit, unit_price: imp.unit_price };
            updated++;
          } else {
            merged.push(imp);
            if (c) idxByCode[c] = merged.length - 1;
            added++;
          }
        });
        return { ...prev, voci: merged.length ? merged : [emptyVoce()] };
      });
      const parts = [];
      if (data.count) parts.push(`${data.count} voci lette`);
      if (data.skipped) parts.push(`${data.skipped} righe ignorate`);
      toast.success(parts.join(" · ") || "Import completato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante l'importazione");
    }
    setImporting(false);
  };

  useEffect(() => {
    if (!id) return;
    api.get("/listini").then((r) => {
      const l = r.data.find((x) => x.id === id);
      if (l) setForm({ name: l.name, active: l.active, kind: l.kind || "attivo", voci: l.voci.length ? l.voci.map((v) => ({ ...v })) : [emptyVoce()] });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const setVoce = (i, patch) => setForm((f) => ({ ...f, voci: f.voci.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) }));
  const addVoce = () => setForm((f) => ({ ...f, voci: [...f.voci, emptyVoce()] }));
  const removeVoce = (i) => setForm((f) => ({ ...f, voci: f.voci.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const voci = form.voci.filter((v) => v.description.trim()).map((v) => ({ id: v.id || undefined, code: (v.code || "").trim(), description: v.description, unit: v.unit || "m", unit_price: parseFloat(v.unit_price) || 0 }));
      const payload = { name: form.name, active: form.active, kind: form.kind, voci };
      if (id) await api.put(`/listini/${id}`, payload);
      else await api.post("/listini", payload);
      toast.success(id ? "Listino aggiornato" : "Listino creato");
      navigate("/listini");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSaving(false);
  };

  const priceLabel = form.kind === "passivo" ? "€/costo" : "€/prezzo";

  if (loading) return <div className="mx-auto max-w-3xl p-6 text-slate-400">Caricamento…</div>;

  return (
    <div className="mx-auto max-w-3xl">
      <button onClick={() => navigate("/listini")} data-testid="listino-back-btn" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Torna ai listini</button>
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">{id ? "Modifica listino" : "Nuovo listino"}</h1>
        <p className="mt-1 text-sm text-slate-500">Voci di capitolato con prezzo unitario.</p>
      </div>

      <Card className="rounded-lg border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><Label htmlFor="l-name">Nome listino</Label><Input id="l-name" data-testid="listino-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="mt-1.5" data-testid="listino-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="attivo">Attivo (VDC cliente)</SelectItem>
                <SelectItem value="passivo">Passivo (materiali)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <Label>Voci di capitolato</Label>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn" className="rounded-md"><Download className="mr-1 h-4 w-4" /> Scarica template</Button>
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-excel-btn" className="rounded-md"><Upload className="mr-1 h-4 w-4" /> {importing ? "Importazione…" : "Importa Excel"}</Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onImportFile} data-testid="import-file-input" className="hidden" />
              <Button size="sm" variant="outline" onClick={addVoce} data-testid="add-voce-btn" className="rounded-md"><Plus className="mr-1 h-4 w-4" /> Aggiungi voce</Button>
            </div>
          </div>
          <div className="space-y-2">
            {form.voci.map((v, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`voce-row-${i}`}>
                <Input placeholder="Codice" data-testid={`voce-code-${i}`} value={v.code || ""} onChange={(e) => setVoce(i, { code: e.target.value })} className="w-24 shrink-0" />
                <Input placeholder="Descrizione" data-testid={`voce-desc-${i}`} value={v.description} onChange={(e) => setVoce(i, { description: e.target.value })} className="flex-1" />
                <Select value={v.unit || ""} onValueChange={(val) => setVoce(i, { unit: val })}>
                  <SelectTrigger className="w-28" data-testid={`voce-unit-${i}`}><SelectValue placeholder="Unità" /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                    {v.unit && !units.some((u) => u.name === v.unit) && <SelectItem value={v.unit}>{v.unit}</SelectItem>}
                  </SelectContent>
                </Select>
                <Input placeholder={priceLabel} type="number" step="0.01" data-testid={`voce-price-${i}`} value={v.unit_price} onChange={(e) => setVoce(i, { unit_price: e.target.value })} className="w-28" />
                <button onClick={() => removeVoce(i)} data-testid={`voce-remove-${i}`} className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate("/listini")} className="rounded-md">Annulla</Button>
          <Button onClick={save} disabled={!form.name || saving} data-testid="save-listino-btn" className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><Save className="mr-2 h-4 w-4" /> Salva</Button>
        </div>
      </Card>
    </div>
  );
}
